import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Mail, CheckCircle2 } from 'lucide-react';
import { resendConfirmation } from '../../services/notificationService';
import { useNotification } from '../../notification/NotificationContext';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const initialEmail =
    location.state?.email ||
    localStorage.getItem('pendingEmail') ||
    '';

  const [email] = useState(initialEmail);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startCooldown = () => {
    setCooldown(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (!email) {
      showNotification('No email address to resend to. Please register again.', 'warning');
      return;
    }
    setResending(true);
    try {
      await resendConfirmation(email);
      setResent(true);
      startCooldown();
      showNotification('Confirmation email resent!', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to resend confirmation email.', 'error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="ve-page">
      <div className="ve-card">
        <div className="ve-icon-wrap">
          <Mail size={36} />
        </div>

        <h1>Check your inbox</h1>
        <p className="ve-sub">We sent a confirmation link to:</p>
        <p className="ve-email">{email || 'your email'}</p>

        <div className="ve-steps">
          <div className="ve-step">
            <span className="ve-step-num">1</span>
            <span>Open the email from <strong>SkillOn</strong></span>
          </div>
          <div className="ve-step">
            <span className="ve-step-num">2</span>
            <span>Click <strong>"Confirm Email Address"</strong></span>
          </div>
          <div className="ve-step">
            <span className="ve-step-num">3</span>
            <span>You'll be redirected back to SkillOn</span>
          </div>
        </div>

        {resent && (
          <div className="ve-success">
            <CheckCircle2 size={14} /> Confirmation email resent!
          </div>
        )}

        <button
          className="ve-btn ve-btn-outline"
          onClick={handleResend}
          disabled={resending || cooldown > 0 || !email}
        >
          {resending
            ? 'Sending...'
            : cooldown > 0
              ? `Resend in ${cooldown}s`
              : 'Resend confirmation email'}
        </button>

        <button
          className="ve-btn ve-btn-ghost"
          onClick={() => navigate('/login')}
          type="button"
        >
          Back to login
        </button>

        <p className="ve-foot">
          Wrong email? <Link to="/signup">Register again</Link>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
